import { CSPrimaryBtn } from "@/components/design-system/creatorshield/CSButton";
import { EnfinitySecondaryBtn } from "@/components/design-system/enfinity/EnfinityButton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import ReCAPTCHA from "react-google-recaptcha";
import SignaturePad from "react-signature-canvas";
import Button from "../ui/button";

export default function Sign({
  userData,
  setUserData,
  signatureRef,
  clearSignature,
  undoSignature,
  handleEndStroke,
  errorMessage,
  isLoading,
  handleSignUp,
  setRecaptchaToken,
  errorPrompt,
  isEnfiniShield = false,
}) {
  const landingPage = import.meta.env.VITE_LANDING_PAGE;
  const isEnfinity = landingPage === "enfinity";

  const toggleAllFields = (checked) => {
    setUserData((prev) => ({
      ...prev,
      tos: checked,
      contentId: checked,
      profitGuarantee: checked,
      informationAccurate: checked,
    }));
  };

  const allFieldsChecked =
    userData.tos && userData.contentId && userData.informationAccurate;

  return (
    <>
      <div className="space-y-2 w-full">
        <h1
          className={`text-3xl text-left font-medium ${
            landingPage === "adcelerate"
              ? "text-primary"
              : landingPage === "creatorshield" || isEnfinity
                ? "text-black"
                : "text-white"
          }`}
        >
          Almost Done!
        </h1>
        <p
          className={`text-lg ${
            landingPage === "adcelerate" ? "text-primary" : isEnfinity ? "text-black" : ""
          }`}
        >
          If you are under 18, your parent/guardian must sign on your behalf
        </p>
      </div>

      <div className="flex flex-col gap-6 my-10 w-full">
        <div className="relative">
          <SignaturePad
            ref={signatureRef}
            onEnd={handleEndStroke}
            canvasProps={{
              className: `w-full p-2 h-36 md:h-48 rounded-2xl ${
                landingPage === "adcelerate" ? "bg-white" : "bg-[whiteSmoke]"
              }`,
            }}
          />
          <div className="flex gap-3 absolute top-8 right-10 text-gray-800">
            <button onClick={undoSignature} className="underline text-sm">
              Undo
            </button>
            <button onClick={clearSignature} className="underline text-sm">
              Clear
            </button>
          </div>
          <div className="flex justify-center">
            <hr className="border-[1px] border-gray-300 absolute bottom-7 w-[90%]" />
          </div>
        </div>
        <div className="space-y-2">
          <h1
            className={`font-medium ${
              landingPage === "adcelerate"
                ? "text-primary"
                : landingPage === "creatorshield" || isEnfinity
                  ? "text-black"
                  : "text-white"
            }`}
          >
            Terms of Service
          </h1>
          <div className="flex gap-3">
            <Checkbox
              id="tos"
              checked={userData.tos}
              onCheckedChange={(checked) =>
                setUserData((prev) => ({ ...prev, tos: checked }))
              }
              className={`mt-[3px] bg-white data-[state=checked]:text-white ${
                isEnfiniShield
                  ? "data-[state=checked]:bg-[#8B5CF6]"
                  : landingPage === "boost"
                    ? "data-[state=checked]:bg-[#3B82F6]"
                    : "data-[state=checked]:bg-[var(--primary)]"
              } ${
                landingPage === "adcelerate"
                  ? "border-[#757575]"
                  : landingPage === "creatorshield" || isEnfinity
                    ? "border-black"
                    : ""
              }`}
            />
            <p
              className={`${
                landingPage === "adcelerate" 
                  ? "text-[#757575]" 
                  : landingPage === "creatorshield" || isEnfinity
                    ? "text-black"
                    : "text-white"
              }`}
            >
              I have read and agree to the{" "}
              <a href="/terms-of-service" target="_blank">
                <span
                  className={`underline cursor-pointer ${
                    landingPage === "adcelerate"
                      ? "text-primary"
                      : landingPage === "creatorshield" || isEnfinity
                        ? "text-black"
                        : "text-white"
                  }`}
                >
                  Terms of Service (click to read)
                </span>
              </a>
            </p>
          </div>
        </div>
        {landingPage === "creatorshield" ? (
          <div className="space-y-2">
            <div className="flex gap-3">
              <Checkbox
                id="contentId"
                checked={userData.contentId}
                onCheckedChange={(checked) =>
                  setUserData((prev) => ({
                    ...prev,
                    contentId: checked,
                  }))
                }
                className="mt-[3px] bg-white data-[state=checked]:text-white data-[state=checked]:bg-[var(--primary)] border border-black"
              />
              <p>
                I will not use the YouTube Copyright Match Tool to "takedown"
                re-uploads from this day forward for as long as I am a
                CreatorShield member
              </p>
            </div>
          </div>
        ) : isEnfiniShield ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <h1
                className={`font-medium ${
                  isEnfinity ? "text-black" : "text-white"
                }`}
              >
                Distribute my videos to earn more on MSN and other partner platforms (Optional)
              </h1>
            </div>
            <div className="flex gap-3">
              <Checkbox
                id="contentId"
                checked={userData.contentId}
                onCheckedChange={(checked) =>
                  setUserData((prev) => ({
                    ...prev,
                    contentId: checked,
                  }))
                }
                className={`mt-[3px] bg-white data-[state=checked]:text-white data-[state=checked]:bg-[#8B5CF6] ${
                  isEnfinity ? "border-black" : ""
                }`}
              />
              <p
                className={`${
                  isEnfinity ? "text-black" : ""
                }`}
              >
                Yes, I allow exclusive redistribution and monetization.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <h1
                className={`font-medium ${
                  landingPage === "adcelerate" ? "text-primary" : isEnfinity ? "text-black" : "text-white"
                }`}
              >
                {landingPage === "shield"
                  ? "Distribute my videos to earn more on MSN and other partner platforms (Optional)"
                  : "Earn more by protecting your videos on YouTube with Content ID (Optional)?"}
              </h1>
              {(landingPage === "adcelerate" || landingPage === "boost") && (
                <Tooltip>
                  <TooltipTrigger>
                    <Info className={`w-5 h-5 ${
                      landingPage === "boost" ? "text-[#3B82F6]" : "text-primary"
                    }`} />
                  </TooltipTrigger>
                  <TooltipContent className="bg-gradient-to-r from-[#60A5FA] to-[#3B82F6] border-0">
                    <p>
                      If you've opted into our YouTube Content ID add-on, please
                      <br />
                      provide the URLs of any other YouTube channels you own or
                      manage
                      <br />
                      that should be whitelisted. This will help ensure your
                      channels
                      <br />
                      are protected and avoid unnecessary Content ID claims.
                    </p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            <div className="flex gap-3">
              <Checkbox
                id="contentId"
                checked={userData.contentId}
                onCheckedChange={(checked) =>
                  setUserData((prev) => ({
                    ...prev,
                    contentId: checked,
                  }))
                }
                className={`mt-[3px] bg-white data-[state=checked]:text-white ${
                  landingPage === "boost"
                    ? "data-[state=checked]:bg-[#3B82F6]"
                    : "data-[state=checked]:bg-[var(--primary)]"
                } ${
                  landingPage === "adcelerate"
                    ? "border-[#757575]"
                    : landingPage === "creatorshield" || isEnfinity
                      ? "border-black"
                      : ""
                }`}
              />
              <p
                className={`${
                  landingPage === "adcelerate" ? "text-[#757575]" : isEnfinity ? "text-black" : ""
                }`}
              >
                {landingPage === "shield"
                  ? "Yes, I allow exclusive redistribution and monetization."
                  : "Yes, I grant express and exclusive permission to claim reuploaded videos"}
              </p>
            </div>
          </div>
        )}
        <div className="space-y-2">
          <h1
            className={`font-medium ${
              landingPage === "adcelerate"
                ? "text-primary"
                : landingPage === "creatorshield" || isEnfinity
                  ? "text-black"
                  : "text-white"
            }`}
          >
            My information is accurate
          </h1>
          <div className="flex gap-3">
            <Checkbox
              id="informationAccurate"
              checked={userData.informationAccurate}
              onCheckedChange={(checked) =>
                setUserData((prev) => ({
                  ...prev,
                  informationAccurate: checked,
                }))
              }
              className={`mt-[3px] bg-white data-[state=checked]:text-white ${
                isEnfiniShield
                  ? "data-[state=checked]:bg-[#8B5CF6]"
                  : landingPage === "boost"
                    ? "data-[state=checked]:bg-[#3B82F6]"
                    : "data-[state=checked]:bg-[var(--primary)]"
              } ${
                landingPage === "adcelerate"
                  ? "border-[#757575]"
                  : landingPage === "creatorshield" || isEnfinity
                    ? "border-black"
                    : ""
              }`}
            />
            <p
              className={`${
                landingPage === "adcelerate" ? "text-[#757575]" : isEnfinity ? "text-black" : ""
              }`}
            >
              I certify that the details I've provided are correct.
            </p>
          </div>
        </div>
        {/* <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 md:gap-0">
          <div className="space-y-2">
            <h1 className="font-medium">There are no profit guarantees</h1>
            <div className="flex gap-3">
              <Checkbox
                id="profitGuarantee"
                checked={userData.profitGuarantee}
                onCheckedChange={(checked) =>
                  setUserData((prev) => ({
                    ...prev,
                    profitGuarantee: checked,
                  }))
                }
                className="mt-[3px] bg-white data-[state=checked]:text-white data-[state=checked]:bg-[var(--primary)]"
              />
              <p>I understand</p>
            </div>
          </div>
        </div> */}
        <div className="flex gap-3">
          <Checkbox
            id="markAll"
            checked={allFieldsChecked}
            onCheckedChange={toggleAllFields}
            className={`mt-[3px] bg-white data-[state=checked]:text-white ${
              isEnfiniShield
                ? "data-[state=checked]:bg-[#8B5CF6]"
                : landingPage === "boost"
                  ? "data-[state=checked]:bg-[#3B82F6]"
                  : "data-[state=checked]:bg-[var(--primary)]"
            } ${
              landingPage === "adcelerate"
                ? "border-[#757575]"
                : landingPage === "creatorshield" || isEnfinity
                  ? "border-black"
                  : ""
            }`}
          />
          <label
            htmlFor="markAll"
            className={`font-bold ${
              landingPage === "adcelerate"
                ? "text-[#757575]"
                : landingPage === "creatorshield" || isEnfinity
                  ? "text-black"
                  : "text-white"
            }`}
          >
            Mark All Fields
          </label>
        </div>

        <div className="mt-4 mx-auto">
          <ReCAPTCHA
            sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
            onChange={(token) => setRecaptchaToken(token)}
            onExpired={() => setRecaptchaToken(null)}
          />
        </div>

        {errorMessage && (
          <div className="text-white bg-red-500 w-fit px-4 py-1 rounded-md mx-auto">
            {errorMessage}
          </div>
        )}

        {errorPrompt && (
          <div className="text-white bg-red-500 w-fit px-4 py-1 rounded-md mx-auto">
            {errorPrompt}
          </div>
        )}
        {landingPage === "creatorshield" ? (
          <CSPrimaryBtn
            onClick={() => handleSignUp()}
            className="rounded-md py-6 w-full shadow mt-6"
            disabled={isLoading}
          >
            {isLoading ? "Processing..." : "Complete Sign up"}
          </CSPrimaryBtn>
        ) : isEnfinity ? (
          <button
          
            onClick={() => handleSignUp()}
            disabled={isLoading}
            className="w-full py-3 mt-6 rounded-3xl font-medium transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-xl bg-gradient-to-r from-[#60A5FA] to-[#3B82F6] text-white hover:from-[#70B5FA] hover:to-[#4B92F6] disabled:opacity-50 disabled:cursor-not-allowed enfinity-subtext-font"
          >
            {isLoading ? "Processing..." : "Complete Sign up"}
          </button>
        ) : (
          <Button
            onClick={() => handleSignUp()}
            className={` text-black py-6 uppercase w-full shadow mt-6 ${
              landingPage === "adcelerate"
                ? "bg-primary text-white rounded-md"
                : landingPage === "boost" ? "bg-gradient-to-r from-[#60A5FA] to-[#3B82F6] text-white hover:from-[#70B5FA] hover:to-[#4B92F6] rounded-full" : landingPage === "shield" ? "bg-[#8B5CF6] text-white rounded-full" : "bg-white text-black rounded-md"
            }`}
            disabled={isLoading}
          >
            {isLoading ? "Processing..." : "Complete Sign up"}
          </Button>
        )}
      </div>
    </>
  );
}
